import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { SETTINGS_DEFAULTS } from "@/hooks/useSettings";
import MarkdownToolbar from "@/components/MarkdownToolbar";
import { toast } from "sonner";

const FIELDS = [
  { key: "hero_title", label: "Title (line breaks allowed)", type: "textarea-small" },
  { key: "hero_subtitle", label: "Subtitle (italic)", type: "text" },
  { key: "hero_copy", label: "Copy", type: "textarea" },
  { key: "footer_copy", label: "Footer copy", type: "textarea" },
  { key: "about_body", label: "About page body (blank lines separate paragraphs)", type: "textarea-large" },
];

export default function SiteSettingsPanel() {
  const [values, setValues] = useState(SETTINGS_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const textareaRefs = useRef({});
  const bgFileRef = useRef(null);
  const heroLeftFileRef = useRef(null);
  const heroRightFileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setValues({ ...SETTINGS_DEFAULTS, ...r.data }));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", values);
      setValues({ ...SETTINGS_DEFAULTS, ...data });
      toast.success("Site copy updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const uploadImageToField = async (file, fieldKey, fileInputRef, successToast) => {
    if (!file) return;
    setUploadingField(fieldKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const nextValues = { ...values, [fieldKey]: data.url };
      const resp = await api.put("/admin/settings", nextValues);
      setValues({ ...SETTINGS_DEFAULTS, ...resp.data });
      toast.success(successToast);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingField(null);
      if (fileInputRef?.current) fileInputRef.current.value = "";
    }
  };

  const uploadBackground = (file) =>
    uploadImageToField(file, "background_url", bgFileRef, "Site background updated.");

  const uploadHeroLeft = (file) =>
    uploadImageToField(file, "hero_bg_left_url", heroLeftFileRef, "Left banner image updated.");

  const uploadHeroRight = (file) =>
    uploadImageToField(file, "hero_bg_right_url", heroRightFileRef, "Right banner image updated.");

  const uploadingBg = uploadingField === "background_url";

  const setBackgroundMode = async (mode) => {
    const nextValues = { ...values, background_mode: mode };
    setValues(nextValues);
    try {
      const { data } = await api.put("/admin/settings", nextValues);
      setValues({ ...SETTINGS_DEFAULTS, ...data });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setHeroBgAlign = async (side, align) => {
    const key = side === "left" ? "hero_bg_left_align" : "hero_bg_right_align";
    const nextValues = { ...values, [key]: align };
    setValues(nextValues);
    try {
      const { data } = await api.put("/admin/settings", nextValues);
      setValues({ ...SETTINGS_DEFAULTS, ...data });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const clearField = async (fieldKey, successToast) => {
    const nextValues = { ...values, [fieldKey]: "" };
    setValues(nextValues);
    try {
      const { data } = await api.put("/admin/settings", nextValues);
      setValues({ ...SETTINGS_DEFAULTS, ...data });
      toast.success(successToast);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const clearBackground = () => clearField("background_url", "Site background removed.");
  const clearHeroLeft = () => clearField("hero_bg_left_url", "Left banner image removed.");
  const clearHeroRight = () => clearField("hero_bg_right_url", "Right banner image removed.");

  return (
    <form onSubmit={save} className="border border-black p-6 space-y-6">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-700">Editable copy</div>
        <p className="text-sm text-neutral-700 mt-2">
          Edit the text that appears on the front page and in the footer. Changes go live instantly.
        </p>
      </div>

      {/* Page background — applies to every page except the masthead nav bar
          and the front-page hero. Saved instantly on upload / mode change. */}
      <div className="border border-black p-4">
        <div className="mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#fc4707" }}>
          Page background
        </div>
        <p className="text-sm text-neutral-700 mt-2">
          Upload an image to sit behind every page. It won&rsquo;t cover the front-page banner or the menu bar.
        </p>
        {values.background_url ? (
          <div className="mt-4 flex items-start gap-4 flex-wrap">
            <div
              className="w-32 h-20 border border-neutral-300 shrink-0"
              style={{
                backgroundImage: `url(${values.background_url})`,
                backgroundSize: values.background_mode === "cover" ? "cover" : "auto",
                backgroundRepeat: values.background_mode === "cover" ? "no-repeat" : "repeat",
                backgroundPosition: "center",
              }}
              data-testid="settings-bg-preview"
              aria-label="Current background preview"
            />
            <div className="flex-1 min-w-0 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Display</span>
                {["tile", "cover"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    data-testid={`settings-bg-mode-${mode}`}
                    onClick={() => setBackgroundMode(mode)}
                    className={`mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
                      (values.background_mode || "tile") === mode
                        ? "bg-black text-white border-black"
                        : "border-neutral-300 hover:border-black"
                    }`}
                  >
                    {mode === "tile" ? "Tile" : "Fill (single image)"}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <label
                  htmlFor="settings-bg-upload"
                  className="mono text-[10px] uppercase tracking-[0.2em] border border-neutral-300 hover:border-black px-3 py-1 cursor-pointer"
                >
                  {uploadingBg ? "Uploading…" : "Replace"}
                </label>
                <button
                  type="button"
                  data-testid="settings-bg-clear"
                  onClick={clearBackground}
                  disabled={uploadingBg}
                  className="mono text-[10px] uppercase tracking-[0.2em] border border-red-700 text-red-700 px-3 py-1 hover:bg-red-700 hover:text-white transition-colors disabled:opacity-40"
                >
                  Remove background
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <label
              htmlFor="settings-bg-upload"
              className="mono text-[10px] uppercase tracking-[0.2em] border border-neutral-300 hover:border-black px-3 py-1 cursor-pointer inline-block"
            >
              {uploadingBg ? "Uploading…" : "Upload background image"}
            </label>
          </div>
        )}
        <input
          id="settings-bg-upload"
          ref={bgFileRef}
          type="file"
          accept="image/*"
          data-testid="settings-bg-upload"
          onChange={(e) => uploadBackground(e.target.files?.[0])}
          className="hidden"
        />
      </div>

      {/* Masthead banner backgrounds — left half and right half sit behind
          the logo / site name / date at the very top of every page. Each has
          its own upload + alignment radio. */}
      <div className="border border-black p-4">
        <div className="mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#fc4707" }}>
          Masthead banner backgrounds
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {["left", "right"].map((side) => {
            const urlKey = side === "left" ? "hero_bg_left_url" : "hero_bg_right_url";
            const alignKey = side === "left" ? "hero_bg_left_align" : "hero_bg_right_align";
            const url = values[urlKey];
            const align = values[alignKey] || "center";
            const inputId = `settings-hero-${side}-upload`;
            const inputRef = side === "left" ? heroLeftFileRef : heroRightFileRef;
            const onUpload = side === "left" ? uploadHeroLeft : uploadHeroRight;
            const onClear = side === "left" ? clearHeroLeft : clearHeroRight;
            const busy = uploadingField === urlKey;
            return (
              <div key={side} className="border border-black p-3">
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  {side === "left" ? "Left half" : "Right half"}
                </div>
                {url ? (
                  <div
                    className="mt-2 h-24 border border-neutral-300"
                    style={{
                      backgroundImage: `url(${url})`,
                      backgroundPosition: `${align} center`,
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                    }}
                    data-testid={`settings-hero-${side}-preview`}
                    aria-hidden
                  />
                ) : (
                  <div className="mt-2 h-24 border border-dashed border-neutral-300 flex items-center justify-center">
                    <span className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">No image</span>
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Align</span>
                  {["left", "center", "right"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`settings-hero-${side}-align-${opt}`}
                      onClick={() => setHeroBgAlign(side, opt)}
                      disabled={!url}
                      className={`mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border transition-colors ${
                        align === opt
                          ? "bg-black text-white border-black"
                          : "border-neutral-300 hover:border-black"
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      {opt === "center" ? "Centre" : opt === "left" ? "Left" : "Right"}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <label
                    htmlFor={inputId}
                    className="mono text-[10px] uppercase tracking-[0.2em] border border-neutral-300 hover:border-black px-3 py-1 cursor-pointer"
                  >
                    {busy ? "Uploading…" : url ? "Replace" : "Upload"}
                  </label>
                  {url && (
                    <button
                      type="button"
                      data-testid={`settings-hero-${side}-clear`}
                      onClick={onClear}
                      disabled={busy}
                      className="mono text-[10px] uppercase tracking-[0.2em] border border-red-700 text-red-700 px-3 py-1 hover:bg-red-700 hover:text-white transition-colors disabled:opacity-40"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  id={inputId}
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  data-testid={`settings-hero-${side}-upload`}
                  onChange={(e) => onUpload(e.target.files?.[0])}
                  className="hidden"
                />
              </div>
            );
          })}
        </div>
      </div>
      {FIELDS.map((f) => (
        <div key={f.key}>
          <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{f.label}</label>
          {f.type === "textarea" || f.type === "textarea-large" || f.type === "textarea-small" ? (
            <>
              <textarea
                ref={(el) => { textareaRefs.current[f.key] = el; }}
                data-testid={`settings-${f.key}`}
                value={values[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value)}
                rows={f.type === "textarea-large" ? 14 : f.type === "textarea-small" ? 2 : 3}
                className="mt-2 w-full border border-neutral-300 focus:border-black bg-transparent p-3 focus:outline-none resize-y"
              />
              <MarkdownToolbar
                textareaRef={{ current: textareaRefs.current[f.key] }}
                value={values[f.key] || ""}
                onChange={(v) => set(f.key, v)}
              />
              {f.key === "hero_title" && (
                <div className="mt-4 flex items-center gap-4 flex-wrap">
                  <span className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    Alignment when published
                  </span>
                  <div className="flex gap-2" data-testid="settings-hero-align">
                    {["left", "center"].map((opt) => {
                      const active = (values.hero_align || "left") === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          data-testid={`settings-hero-align-${opt}`}
                          onClick={() => set("hero_align", opt)}
                          className={`mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
                            active
                              ? "bg-black text-white border-black"
                              : "border-neutral-300 hover:border-black"
                          }`}
                        >
                          {opt === "left" ? "Left" : "Centre"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <input
              data-testid={`settings-${f.key}`}
              value={values[f.key] || ""}
              onChange={(e) => set(f.key, e.target.value)}
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none"
            />
          )}
        </div>
      ))}
      <div className="flex justify-end border-t border-neutral-200 pt-4">
        <button
          type="submit"
          data-testid="settings-save-button"
          disabled={saving}
          className="bg-black text-white px-6 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save site copy"}
        </button>
      </div>
    </form>
  );
}
